import { defineStore } from 'pinia';
import authApi, { publicApi } from './../api/api';
import { useNotification } from "@kyvg/vue3-notification";

const { notify } = useNotification()

// Интерфейсы для типизации профиля пользователя
interface UserFeatures {
    NEW_CALENDAR?: boolean;
    [key: string]: any;
}

interface UserProfile {
    first_name: string;
    last_name: string;
    features?: UserFeatures;
    [key: string]: any;
}

function reformatDate(dateStr: string): string {
    if (typeof dateStr === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
        const [day, month, year] = dateStr.split('/');
        return `${year}-${month}-${day}`;
    }
    return dateStr;
}

export const useAuthStore = defineStore('auth', {
    state: () => ({
        isAuthenticated: false,
        user: null as UserProfile | null, // Применяем типизацию
        token: localStorage.getItem('token') || null,
        error: null as string | null,
    }),

    actions: {
        async fetchProfile() {
            if (!this.token) {
                return;
            }
            try {
                const response = await authApi.get('/student/profile');

                const userData = response.data;
                if (userData && typeof userData === 'object') {
                    for (const key in userData) {
                        if (Object.prototype.hasOwnProperty.call(userData, key)) {
                            // Пропускаем объект features, форматируем только даты
                            if (key !== 'features') {
                                userData[key] = reformatDate(userData[key]);
                            }
                        }
                    }
                }

                this.user = userData;
                this.isAuthenticated = true;
            } catch (error) {
                console.error("Failed to fetch profile:", error);
            }
        },

        async login({ email, password }: any) {
            try {
                const response = await publicApi.post(`/student/auth/signin`, {
                    email,
                    password,
                });
                this.token = response.data.accessToken;

                if (this.token) {
                    localStorage.setItem('token', this.token);
                    localStorage.setItem('refreshToken', response.data.refreshToken)
                    await this.fetchProfile();
                }
                this.error = null;
                return { success: true };
            } catch (error: any) {
                this.error = error.response?.data?.message || 'Ошибка входа';
                return { success: false, error: this.error };
            }
        },

        logout() {
            this.isAuthenticated = false;
            this.user = null;
            this.token = null;
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            window.location.href = '/login';
        },

        async changePassword({ oldPassword, newPassword }: any) {
            try {
                await authApi.patch(`/v1/student/me/password`, {
                    oldPassword,
                    newPassword
                });
                this.error = null;
                return { success: true };
            } catch (error: any) {
                this.error = error.response?.data?.message || 'Ошибка смены пароля';
                if (this.error !== null) {
                    notify({
                        title: "Ошибка",
                        text: this.error,
                        type: "error"
                    });
                }
                return { success: false, error: this.error };
            }
        },

        async recoverPassword(email: string) {
            try {
                await publicApi.post(`/student/auth/forgot-password`, { email });
                this.error = null;
                return { success: true };
            } catch (error: any) {
                this.error = error.response?.data?.message || 'Ошибка восстановления пароля';
                if (this.error !== null) {
                    notify({
                        title: this.error,
                        type: "error"
                    });
                }
                return { success: false, error: this.error };
            }
        },

        async resetPassword({ token, password }: any) {
            try {
                await publicApi.post(`/student/auth/reset-password`, {
                    token,
                    password
                });
                this.error = null;
                return { success: true };
            } catch (error: any) {
                this.error = error.response?.data?.message || 'Ошибка восстановления пароля'
                if (this.error !== null) {
                    notify({
                        title: this.error,
                        type: "error"
                    });
                }
                return { success: false, error: this.error };
            }
        },

        initializeAuth() {
            const token = localStorage.getItem('token');
            if (token) {
                this.token = token;
                this.fetchProfile();
            }
        },
    },

    getters: {
        isLoggedIn: (state) => state.isAuthenticated,
        currentUser: (state) => state.user,
        authError: (state) => state.error,
    },
});